import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, useArtistSearch } from '../lib/api';
import { genreMeta, splitMatch } from '../lib/artists';
import { useRecentSearches } from '../lib/localPrefs';
import { useDebounced } from '../lib/useDebounced';
import { ArtistAvatar } from './Artist';
import { SearchIcon } from './Icons';
import { NoMatches } from './NoMatches';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Desktop header search: an ARIA 1.2 combobox with up to 6 artist matches (FR-G2). */
export function HeaderSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const navigate = useNavigate();
  const { push } = useRecentSearches();
  const debounced = useDebounced(q);
  const { data, isFetching, isError, error } = useArtistSearch(debounced);
  const results = debounced.trim() ? (data?.artists ?? []) : [];
  const showList = open && q.trim().length > 0;

  // ⌘K / Ctrl+K focuses search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const go = (i: number) => {
    const a = results[i];
    if (!a) return;
    push({ id: a.id, name: a.name });
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    navigate(`/artist/${a.id}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(active >= 0 ? active : 0);
    } else if (e.key === 'Escape') {
      if (open && q) setOpen(false);
      else setQ('');
    }
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;
  const stale = q.trim() !== debounced.trim() || isFetching;

  return (
    <div style={{ position: 'relative', flex: 1, maxWidth: 560 }}>
      <label className="search" style={{ height: 48 }}>
        <SearchIcon />
        <span className="visually-hidden">Search an artist</span>
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder="Search an artist"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
        <kbd className="kbd" aria-hidden="true">
          {isMac ? '⌘K' : 'Ctrl K'}
        </kbd>
      </label>

      {showList && (
        <div
          className="popover"
          style={{ position: 'absolute', left: 0, right: 0, top: 56, zIndex: 20, padding: 8 }}
          // Keep focus in the input while clicking a result.
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul id={listId} role="listbox" aria-label="Artists" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {results.map((a, i) => {
              const [pre, hit, post] = splitMatch(a.name, debounced);
              return (
                <li
                  key={a.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === active}
                  onClick={() => go(i)}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 10px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    background: i === active ? 'var(--raised-2)' : undefined,
                  }}
                >
                  <ArtistAvatar name={a.name} image={a.image} size={40} />
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="ellipsis" style={{ fontWeight: 600 }}>
                      {pre}
                      <mark>{hit}</mark>
                      {post}
                    </span>
                    <span className="mono muted ellipsis" style={{ fontSize: 12 }}>
                      {genreMeta(a.genres)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
          {!stale && !isError && results.length === 0 && (
            <NoMatches q={q} fixture={data?.fixture} style={{ margin: 10 }} />
          )}
          {isError && (
            <p className="muted" role="alert" style={{ margin: 10 }}>
              {errorMessage(error)}
            </p>
          )}
          {stale && results.length === 0 && !isError && (
            <p className="muted" style={{ margin: 10 }}>
              Searching…
            </p>
          )}
        </div>
      )}
    </div>
  );
}
